import { Box, Button, Skeleton, Stack, Typography, Chip } from "@mui/material";
import React, { FC, useState } from "react";
import useResponsive from "../../hooks/useResponsive";
import { JobIdDetailsProps } from "../../interface/job_board";
import { formatDate } from "../../utils/url-id";
import { JobEmpStatus } from "./JobTable";
import { textSty } from "../UIElements/styles-typography";
import { JobUpdateModal } from "./JobUpdateModal";
import WorkIcon from "@mui/icons-material/Work";

const JobHeading: FC<{
  jobDetails: JobIdDetailsProps;
  loading: boolean;
  modalStatus: string;
  setModalStatus: React.Dispatch<React.SetStateAction<string>>;
  jobId: string;
}> = ({ jobDetails, loading, jobId, modalStatus, setModalStatus }) => {
  const isMedium = useResponsive("down", "md", "xl");
  const [open, setOpen] = useState(false);
  const handleModal = () => setOpen(!open);

  const headerSty = {
    fontFamily: "DM Sans",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "18px",
    letterSpacing: "-0.23999999463558197px",
    textAlign: "left",
    color: "#404E57",
    marginBottom: 1,
  };

  return (
    <>
      <JobUpdateModal
        open={open}
        handleModal={handleModal}
        jobId={jobId}
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
      />
      <Box>
        {/* Title and Buttons Section */}
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 2 }}
        >
          {!loading ? (
            <Stack
              sx={{ px: 2, width: "100%" }}
              direction="row"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography
                  variant="h3"
                  sx={{ color: "#000", fontWeight: 700 }}
                >
                  {jobDetails.job_title}
                </Typography>

                {/* Show outsourced badge next to title */}
                {jobDetails.is_outsourced && (
                  <Chip
                    icon={<WorkIcon sx={{ fontSize: "16px !important" }} />}
                    label="Outsourced Hiring"
                    sx={{
                      backgroundColor: "#E3F2FD",
                      color: "#1976D2",
                      fontWeight: 600,
                      fontSize: "12px",
                      border: "1.5px solid #2196F3",
                      "& .MuiChip-icon": {
                        color: "#1976D2",
                      },
                    }}
                  />
                )}
              </Stack>

              {/* Button Group */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleModal}
                  sx={{ fontWeight: 700 }}
                >
                  Update status
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Skeleton
              variant="rounded"
              width={"70%"}
              height={30}
              sx={{ px: 3, ml: 3 }}
            />
          )}
        </Stack>

        {/* Job Details Section */}
        {!loading ? (
          <Stack direction={"row"} sx={{ flexWrap: "wrap", px: 2, mt: 2 }}>
            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Department: </Typography>
              <Typography sx={{ ...textSty }}>
                {jobDetails?.department}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}

            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Job Type: </Typography>
              <Typography sx={{ ...textSty }}>
                {jobDetails?.job_type}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}

            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Posting Date:</Typography>
              <Typography sx={{ ...textSty }}>
                {formatDate(jobDetails?.createdAt)}{" "}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}

            <Stack direction="row" sx={{ gap: 1 }}>
              <Typography sx={{ ...headerSty }}>Status:</Typography>
              <JobEmpStatus status={modalStatus ?? ""} />
            </Stack>

            {/* Show outsourcing status inline if job is outsourced */}
            {jobDetails.is_outsourced && (
              <>
                {!isMedium && (
                  <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>
                )}
                <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
                  <Typography sx={{ ...headerSty }}>Hiring:</Typography>
                  <Typography
                    sx={{
                      ...textSty,
                      color: "#1976D2",
                      fontWeight: 600,
                    }}
                  >
                    Managed by Team
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        ) : (
          <Skeleton
            variant="rounded"
            width={"75%"}
            height={20}
            sx={{ px: 3, my: 2, ml: 3 }}
          />
        )}

        {/* Outsourcing Details Card - Show below main details if outsourced */}
        {!loading &&
          jobDetails.is_outsourced &&
          jobDetails.outsourced_request && (
            <Box
              sx={{
                mx: 2,
                mt: 3,
                p: 3,
                borderRadius: "12px",
                backgroundColor: "#F5F9FF",
                border: "1px solid #E3F2FD",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <WorkIcon sx={{ color: "#1976D2", fontSize: "20px" }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: "Lexend",
                    fontWeight: 600,
                    color: "#1976D2",
                  }}
                >
                  Outsourced Hiring Details
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2}>
                  <Typography sx={{ ...headerSty, minWidth: "140px" }}>
                    Positions Needed:
                  </Typography>
                  <Typography sx={{ ...textSty }}>
                    {jobDetails.outsourced_request.outsourcing_details
                      ?.positions_needed || "N/A"}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Typography sx={{ ...headerSty, minWidth: "140px" }}>
                    Urgency Level:
                  </Typography>
                  <Chip
                    label={
                      jobDetails.outsourced_request.outsourcing_details?.urgency?.toUpperCase() ||
                      "MEDIUM"
                    }
                    size="small"
                    sx={{
                      backgroundColor:
                        jobDetails.outsourced_request.outsourcing_details
                          ?.urgency === "critical"
                          ? "#FFEBEE"
                          : jobDetails.outsourced_request.outsourcing_details
                                ?.urgency === "high"
                            ? "#FFF3E0"
                            : "#E8F5E9",
                      color:
                        jobDetails.outsourced_request.outsourcing_details
                          ?.urgency === "critical"
                          ? "#C62828"
                          : jobDetails.outsourced_request.outsourcing_details
                                ?.urgency === "high"
                            ? "#E65100"
                            : "#2E7D32",
                      fontWeight: 600,
                      fontSize: "11px",
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Typography sx={{ ...headerSty, minWidth: "140px" }}>
                    Request Status:
                  </Typography>
                  <Typography sx={{ ...textSty, color: "#1976D2" }}>
                    {jobDetails.outsourced_request.status || "Pending"}
                  </Typography>
                </Stack>

                {jobDetails.outsourced_request.outsourcing_details
                  ?.special_requirements && (
                  <Stack direction="row" spacing={2}>
                    <Typography sx={{ ...headerSty, minWidth: "140px" }}>
                      Requirements:
                    </Typography>
                    <Typography sx={{ ...textSty, flex: 1 }}>
                      {
                        jobDetails.outsourced_request.outsourcing_details
                          .special_requirements
                      }
                    </Typography>
                  </Stack>
                )}

                {jobDetails.outsourced_request.stats && (
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Typography sx={{ ...headerSty, minWidth: "140px" }}>
                      Progress:
                    </Typography>
                    <Stack direction="row" spacing={3}>
                      <Typography sx={{ fontSize: "13px" }}>
                        <strong>
                          {jobDetails.outsourced_request.stats
                            .candidates_shortlisted || 0}
                        </strong>{" "}
                        shortlisted
                      </Typography>
                      <Typography sx={{ fontSize: "13px" }}>
                        <strong>
                          {jobDetails.outsourced_request.stats
                            .positions_filled || 0}
                        </strong>{" "}
                        positions filled
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
      </Box>
    </>
  );
};

export default JobHeading;
